import { Metadata, getEnumMetadataFromMap } from 'lib/rich/metadata'
import { InstanceProperties, RichType } from 'lib/rich/rich-type'
import { UnitShorthand } from 'types/unit-shorthand'
import { unit_metadata } from './common'

const oversize: Omit<Metadata, 'title'> = {
  kind: 'length',
  default_value_in: 0.03,
  default_value_mm: 0.5,
  required: true,
  gte: 0.0,
  inGroup: true,
}

const roundToNearest: Omit<Metadata, 'title'> = {
  kind: 'length',
  default_value_in: 0.25,
  default_value_mm: 5.0,
  required: true,
  gte: 0.0,
  inGroup: true,
}

const typeEnumValues = getEnumMetadataFromMap({
  relativeBox: 'Box',
  relativeCylinder: 'Cylinder',
})

const BOX = { enabledWhen: { type: { eq: 'relativeBox' } } }
const CYLINDER = { enabledWhen: { type: { eq: 'relativeCylinder' } } }

export class Stock extends RichType {
  static type_metadata: Metadata = {
    title: 'Type',
    kind: 'enum',
    enumValues: typeEnumValues,
    default_value: 'relativeBox',
    required: true,
  }

  static unit_metadata: Metadata = unit_metadata
  unit: UnitShorthand

  // Grip Stock

  static gripStockGroup_metadata: Metadata = {
    kind: 'group',
    title: 'Workholding Allowance',
    description:
      'Specify the amount of stock that should be gripped by the vise jaws.',
  }

  static gripStock_metadata: Metadata = {
    title: 'Grip Stock',
    kind: 'length',
    default_value_in: 0.25,
    default_value_mm: 5.0,
    required: true,
    gte: 0.0,
    inGroup: true,
  }
  gripStock: number

  // Stock Oversize

  static oversize_metadata: Metadata = {
    kind: 'group',
    title: 'Stock Oversize',
    description:
      "Specify the minimum amount of stock oversize allowance from the part's bounding box.",
  }

  static diaOversize_metadata: Metadata = {
    title: 'Diameter Oversize',
    inlineTitle: 'Diameter',
    ...oversize,
    ...CYLINDER,
  }
  diaOversize: number

  static lengthOversize_metadata: Metadata = {
    title: 'Length Oversize',
    inlineTitle: 'Length',
    ...oversize,
    ...CYLINDER,
  }
  lengthOversize: number

  static xOversize_metadata: Metadata = {
    title: 'X Oversize',
    inlineTitle: 'X Axis',
    ...oversize,
    ...BOX,
  }
  xOversize: number

  static yOversize_metadata: Metadata = {
    title: 'Y Oversize',
    inlineTitle: 'Y Axis',
    ...oversize,
    ...BOX,
  }
  yOversize: number

  static zOversize_metadata: Metadata = {
    title: 'Z Oversize',
    inlineTitle: 'Z Axis',
    ...oversize,
    ...BOX,
  }
  zOversize: number

  // Round to nearest

  static rounding_metadata: Metadata = {
    kind: 'group',
    title: 'Rounding Increment',
    description:
      'Specify the increments at which stock sizes will be selected in each axis. Toolpath will always round up from the minimum oversize amount.',
  }

  static diaRoundedToNearest_metadata: Metadata = {
    title: 'Diameter Rounded to Nearest',
    inlineTitle: 'Diameter',
    ...roundToNearest,
    ...CYLINDER,
  }
  diaRoundedToNearest: number

  static lengthRoundedToNearest_metadata: Metadata = {
    title: 'Length Rounded to Nearest',
    inlineTitle: 'Length',
    ...roundToNearest,
    ...CYLINDER,
  }
  lengthRoundedToNearest: number

  static xRoundedToNearest_metadata: Metadata = {
    title: 'X Rounded to Nearest',
    inlineTitle: 'X Axis',
    ...roundToNearest,
    ...BOX,
  }
  xRoundedToNearest: number

  static yRoundedToNearest_metadata: Metadata = {
    title: 'Y Rounded to Nearest',
    inlineTitle: 'Y Axis',
    ...roundToNearest,
    ...BOX,
  }
  yRoundedToNearest: number

  static zRoundedToNearest_metadata: Metadata = {
    title: 'Z Rounded to Nearest',
    inlineTitle: 'Z Axis',
    ...roundToNearest,
    ...BOX,
  }
  zRoundedToNearest: number

  static version = 1
}

export type StockData = InstanceProperties<Stock>
